import { ECurrency } from "./common";
import { ICancellationDetail } from "./order";
import { ICalculatedPrice, II18nData, IModel } from "./types";
// https://dev-y-open-doc.charmdeer.com/Hotel.html
export interface IHotelSearchPreferred {
  language?: string;
  currency?: ECurrency[keyof ECurrency];
  version?: string;
}

export interface IHotelSearchStay {
  date: {
    checkIn: string;
    checkOut: string;
  };
  guest: {
    numberOfRooms?: number;
    numberOfAdults: number;
    numberOfChildren?: number;
  };
}

export interface IHotelSearchFilter {
  _biz?: any;
  city?: {
    _id: string;
  };
  hotels?: Array<{
    _id: string;
  }>;
  brands?: Array<{
    _id: string;
  }>;
  hotelTags?: any[];
  benefits?: IHotelBenefit[];
  search?: string;
  promotions?: IHotelPromotion[];
  commissionPromotion?: boolean;
}

export enum EHotelSearchSort {
  /** 按系统推荐排序 */
  Recommendation = "byRecommendation",
  /** 按用户使用记录及系统推荐排序。仅 user 有值时有效 */
  UserRecommendation = "byUserRecommendation",
  /** 按最低单晚价格升序。由于价格是实时向供应商查询，该排序会导致查询性能明显下降 */
  MinBasePrice = "byMinBasePrice",
  /** 按最低单晚价格倒序。由于价格是实时向供应商查询，该排序会导致查询性能明显下降 */
  MinBasePriceDescending = "byMinBasePriceDescending",
  /** 按距离远近排序，仅在传入 filter.geospatial 时有效 */
  Distance = "byDistance",
  /** 按搜索关键字匹配度排序，传入 filter.search 时有效 */
  Search = "bySearch"
}

export interface IHotelBenefit {
  code?: string;
  name?: string;
  type?: string;
  description?: string;
  tags?: Array<string>;
}

export interface IHotelPromotion {
  type: string;
  name: string;
  description: string;
  validCategory: string;
  nameIcon: string;
}

export interface IHotelPriceDetail {
  currency: string;
  amount: number;
  approximate: {
    currency: string;
    exchangeRate: number;
  };
}
export interface IHotelBrandRaw extends IModel {
  hot: boolean;
  imageDetail: {
    bannerBackground: string;
    bannerLogo: string;
    entry: string;
    mapLogo: string;
    mapLogoHighlighted: string;
    statisticsLogo: string;
  };
  nameI18n: II18nData;
  shopGroupRef: IHotelBrandRaw;
  group?: {
    _id: string;
    name: string;
  };
}

export interface IHotelBrand {
  charmdeerRaw?: {
    shopBrand: IHotelBrandRaw;
  };
  name: string;
  _id: string;
}
export interface IVideo {
  coverUrl: string;
  info: Record<string, any>;
  streamingUrl: string;
  url: string;
}
export interface IHotel {
  _id: string;
  name: string;
  nameEn: string;
  chain: {
    code: string;
    name: string;
  };
  city: {
    name: string;
    _id: string;
    country: {
      name: string;
    };
  };
  group: any;
  brand: IHotelBrand;
  images: string[];
  videos: IVideo[];
  code: string;
  description: string;
  address: string;
  telephone: string;
  checkInTime: string;
  checkOutTime: string;
  location: {
    /** 纬度 */
    latitude: number;
    /** 经度 */
    longitude: number;
  };
  benefits: IHotelBenefit[];
  promotions: IHotelPromotion[];
  minBasePrice: number;
  minBasePriceDetail: IHotelPriceDetail;
  _biz?: {
    loading?: boolean;
    [key: string]: any;
    connectingRooms: String;
    petsPolicy: String;
    familyPolicy: String;
  };
}

export enum IHotelPaymentType {
  /** 预付 */
  Prepay = "prepay",
  /** 到店支付 */
  PayOnSite = "payOnSite"
}

export interface IHotelCancellation {
  /** 可否取消 */
  cancelable: boolean;
  /** 描述 */
  description: string;
  /** 截止时间 YYYY-MM-DD HH:mm:ss，按酒店当地时区 */
  deadline: string;
}

export interface IHotelRoomRateRule {
  key: string;
  /** 可接受的支付方式 */
  acceptedPaymentTypes: IHotelPaymentType[];
  /** 可接受的信用卡 */
  acceptedCreditCardTypes: string[];
  /** 取消 */
  cancellation: IHotelCancellation;
  /** 取消政策 */
  cancellationPrepay: IHotelCancellation;
  rawDescription: {
    cancellation: string;
    rateDescription: string;
  };
  _biz: {
    [key: string]: any;
    cancellationDetailPrepay: ICancellationDetail;
  };
}

export interface IHotelRoomRate {
  _id: string;
  name: string;
  nameEn: string;
  nameDetail: {
    translationSrc: string;
    roomRateRaw: string;
  };
  /**
   * 支持的支付方式
   */
  acceptedPaymentTypes: IHotelPaymentType[];
  /** 权益 */
  benefit: {
    code: string;
    type: string;
    description: string;
    tags: string[];
  };
  /** 早餐 */
  breakfast: {
    include: boolean;
    description: string;
  };
  /* 取消政策 */
  cancellation: IHotelCancellation;
  cancellationPrepay: any;
  code: string;
  price: number;
  priceDetail: ICalculatedPrice;
  discount?: {
    category: string;
  };
  _biz?: {
    [key: string]: any;
  };
}
export interface IHotelRoomAttributes {
  /** 床型 */
  bed: string;
  /** 床型类型 */
  bedType: string;
  /** 容纳人数 */
  capacity: number;
  /** 房间面积 */
  size: number;
  /** 景观 */
  view: string;
  /** 床型描述 */
  bedDescription?: string;
}
export interface IHotelRoom {
  _id: string;
  name: string;
  nameEn: string;
  images: string[];
  videos: IVideo[];
  description: string;
  attributes: IHotelRoomAttributes;
  rates: IHotelRoomRate[];
  _biz?: {
    /** 最低价格的rate */
    _minRate?: IHotelRoomRate;
    [key: string]: any;
  };
}

/** 酒店会员礼遇类型 */
export enum EHotelBenefitType {
  /** 尊享礼遇 */
  LUXURY = "luxury",
  /** 环球 */
  VIRTUOSO = "virtuoso"
}

/**
 * 筛选酒店房型标签
 */
export interface IHotelRoomTag {
  label: string;
  type: string;
  value: string;
}

/** 酒店品牌类型id */
export enum EHotelBrandsTypesId {
  /** 其他 **/
  others = "6699c8fb5a09a65fda534d2e"
}

/**
 * 酒店房型排序类型
 */
export enum EHotelRateSortType {
  /** 礼遇优先 */
  BY_BENEFIT = "byBenefit",
  /** 低价优先 */
  BY_LOW_PRICE = "byLowPrice",
  /** 高佣优先 */
  BY_HIGH_COMMISSION = "byHighCommission"
}
